import { LioAireLogo } from "@lio-aire/ui";
import { useOktaAuth } from "@okta/okta-react";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import { z } from "zod";
import { safeReturnPath } from "../auth/return-path.js";

const ReturnStateSchema = z.object({ from: z.string().optional(), reason: z.string().optional() });

/**
 * Platform sign-in landing. Reached when an unauthenticated user hits any guarded route. The Okta
 * PKCE redirect is normally already in flight (RequireAuth triggers it); this page offers an
 * explicit affordance to (re)start it. The Okta SDK handles the shared `/auth/callback` exchange.
 *
 * Uses the shared `@lio-aire/ui` `LioAireLogo` so sign-in carries the same platform branding as the
 * chrome (no per-app logo asset).
 */
export function SignIn(): JSX.Element {
  const { oktaAuth } = useOktaAuth();
  const location = useLocation();
  const state = ReturnStateSchema.safeParse(location.state);
  const originalUri = safeReturnPath(state.success ? state.data.from : undefined);
  const sessionEnded = state.success && state.data.reason === "session-ended";
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <section className="mx-auto max-w-md p-8 text-center">
      <LioAireLogo className="mx-auto h-auto w-64" />
      <h1 className="mt-6 text-xl font-semibold">Sign in to AIRE</h1>
      <p className="mt-2 text-aire-muted">
        {sessionEnded
          ? "Your session has ended. Sign in again to return to your page."
          : "You need to sign in with your LIO Okta account to continue."}
      </p>
      <button
        type="button"
        disabled={starting}
        className="mt-6 rounded bg-aire-accent px-4 py-2 font-medium text-white hover:opacity-90"
        onClick={() => {
          setStarting(true);
          setFailed(false);
          void oktaAuth.signInWithRedirect({ originalUri }).catch(() => {
            setStarting(false);
            setFailed(true);
          });
        }}
      >
        {starting ? "Connecting to Okta…" : "Sign in with Okta"}
      </button>
      {failed ? (
        <p role="alert" className="mt-3 text-aire-danger">
          Could not start sign-in. Please try again.
        </p>
      ) : null}
    </section>
  );
}
